import React, { useState } from "react";
import { api } from "../lib/api";
import { Check, Mail } from "lucide-react";

/**
 * Newsletter signup. Two visual modes:
 *  variant="editorial" (default): wide editorial banner suitable for Home/Article pages
 *  variant="compact":  slim single-line version suitable for Footer
 */
export default function NewsletterSignup({ variant = "editorial", className = "" }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Digite um email válido");
      return;
    }
    setState("loading");
    try {
      await api.post("/newsletter/subscribe", { email, name, source: variant });
      setState("done");
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.detail || "Não foi possível cadastrar. Tente de novo.");
    }
  };

  if (variant === "compact") {
    return (
      <form
        onSubmit={submit}
        data-testid="newsletter-form-compact"
        className={`flex flex-col sm:flex-row items-stretch gap-2 w-full ${className}`}
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={state === "done"}
          placeholder="Seu melhor email"
          aria-label="Seu email"
          className="flex-1 bg-white/10 border border-white/20 text-white placeholder-white/40 px-4 py-2 text-sm focus:outline-none focus:border-orion-orange"
          data-testid="newsletter-email-compact"
          required
        />
        <button
          type="submit"
          disabled={state === "loading" || state === "done"}
          className="bg-orion-orange text-white px-5 py-2 text-[11px] uppercase tracking-widest font-semibold hover:bg-[#E04828] disabled:opacity-60 transition-colors"
          data-testid="newsletter-submit-compact"
        >
          {state === "done" ? "Inscrita" : state === "loading" ? "Enviando..." : "Inscrever"}
        </button>
        {error && <div className="text-xs text-red-300 sm:absolute sm:mt-11">{error}</div>}
      </form>
    );
  }

  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-br from-[#2C1139] via-[#3d1b52] to-[#1c0a25] text-white px-6 md:px-12 py-14 ${className}`}
      data-testid="newsletter-editorial"
      aria-label="Assine a newsletter do Viagens Orion"
    >
      <span className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-orion-orange/25 blur-3xl" aria-hidden />
      <span className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-orion-gold/20 blur-3xl" aria-hidden />

      <div className="relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-orion-gold font-semibold">
          <Mail size={14} /> Newsletter · Viagens Orion
        </div>
        <h2 className="mt-3 font-serif text-xl md:text-4xl leading-tight">
          Receba os próximos roteiros direto no seu email
        </h2>
        <p className="mt-2 md:mt-3 text-white/75 text-sm md:text-base">
          Um email por vez, sem spam. Segredos de destino, cupons exclusivos e as histórias que a Sandra e o Lucas trazem das viagens.
        </p>

        {state === "done" ? (
          <div
            className="mt-8 inline-flex items-center gap-2 bg-white/10 border border-white/20 px-5 py-3 text-sm"
            data-testid="newsletter-success"
          >
            <Check size={16} className="text-emerald-300" />
            Prontinho! Você já está na nossa mala.
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-8 max-w-xl mx-auto grid sm:grid-cols-[1fr,220px] gap-3"
            data-testid="newsletter-form"
          >
            <div className="grid gap-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome (opcional)"
                aria-label="Seu nome"
                className="bg-white/5 border border-white/20 px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-orion-orange"
                data-testid="newsletter-name"
              />
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  aria-label="Seu email"
                  className="w-full bg-white/5 border border-white/20 pl-9 pr-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-orion-orange"
                  data-testid="newsletter-email"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={state === "loading"}
              className="bg-orion-orange text-white px-6 py-3 text-xs uppercase tracking-widest font-semibold hover:bg-[#E04828] transition-colors disabled:opacity-60"
              data-testid="newsletter-submit"
            >
              {state === "loading" ? "Enviando..." : "Quero receber"}
            </button>
          </form>
        )}
        {error && <div className="mt-3 text-xs text-red-300" role="alert">{error}</div>}
        <div className="mt-4 text-[10px] uppercase tracking-widest text-white/45">
          Ao se inscrever você concorda em receber emails do Viagens Orion · Você pode sair a qualquer momento.
        </div>
      </div>
    </section>
  );
}
